import React, { useState } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Link } from '@vk-hr-tek/ui/Link';

import { modules } from '@app/modules';

import { Notification } from '../../../types';

import { NotificationItemHeader } from './NotificationItemHeader';
import { NotificationItemBody } from './NotificationItemBody';
import { useStyles } from './NotificationItem.styles';

export interface NotificationItemProps {
  data: Notification;
  variant?: 'list' | 'plugin';
  onReadClick?: (id: string) => void;
  onClose?: () => void;
}

export const NotificationItem = ({
  data: {
    id,
    icon,
    notifiableName,
    notifiableNick,
    actionName,
    actionNick,
    message,
    isRead,
    createdAt,
    url,
    payload,
  },
  variant = 'list',
  onReadClick,
  onClose,
}: NotificationItemProps) => {
  const { classes } = useStyles();

  const [isMarkReadTooltipOpen, setIsMarkReadTooltipOpen] = useState(false);

  const isWidgetVariant = variant === 'plugin';

  const handleReadClick = (
    event?: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    onReadClick?.(id);
  };

  const handleMarkReadTooltipOpen = () => {
    setIsMarkReadTooltipOpen(true);
  };

  const handleMarkReadTooltipClose = () => {
    setIsMarkReadTooltipOpen(false);
  };

  const handleLinkClick = () => {
    handleReadClick();

    if (onClose) {
      onClose();
    }
  };

  const iconWrapperSize = isWidgetVariant ? '40px' : '48px';

  const Icon = icon;

  const content = (
    <Box
      display="flex"
      gap="12"
      p="8"
      radius="m"
      className={classes.wrapper}
      cursor={url ? 'pointer' : 'default'}
    >
      <Box
        width={iconWrapperSize}
        height={iconWrapperSize}
        radius="round"
        bgcolor="accent.bg.blue"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexShrink="0"
      >
        {Icon && <Icon />}
      </Box>

      <Tooltip
        title={
          <Box display="flex" flexDirection="column" gap="4">
            {message}
            <Typography variant="caption" color="text.light.tertirary">
              {notifiableName} • {actionName}
            </Typography>
          </Box>
        }
        placement="bottom"
        disabled={!isWidgetVariant || isMarkReadTooltipOpen}
      >
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          {...(!isWidgetVariant && { gap: '2' })}
        >
          <NotificationItemHeader
            variant={variant}
            name={notifiableName}
            action={actionName}
            createdAt={createdAt}
          />
          <NotificationItemBody
            variant={variant}
            message={message}
            read={isRead}
            onRead={handleReadClick}
            onTooltipOpen={handleMarkReadTooltipOpen}
            onTooltipClose={handleMarkReadTooltipClose}
          />
        </Box>
      </Tooltip>
    </Box>
  );

  switch (actionNick) {
    case 'community_post_delete':
    case 'community_moderation_post_delete':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_post_suggest':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}?authorship=moderation`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_post_reject':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}?authorship=suggested`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_post_publication':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}/post/${payload.post_id}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'survey_start':
    case 'survey_remind':
      if (!payload?.survey_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.surveys.basename}/${payload.survey_id}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'survey_end':
      if (!payload?.survey_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.surveys.basename}/${payload.survey_id}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_new_post':
    case 'community_post_mention':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}/post/${payload.post_id}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_comment_mention':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}/post/${payload.post_id}#mention-${payload.comment_id}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_new_comment':
    case 'community_comment_reply':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}/post/${payload.post_id}#comment-${payload.comment_id}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_application':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}/manage?filter_status=join_request`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );
  }

  switch (notifiableNick) {
    case 'news':
    case 'comment':
      if (!payload?.news_id) return content;

      const commentId = payload.comment_id;
      const newsUrl = `/${modules.news.basename}/${payload.news_id}${
        commentId ? `#mention-${commentId}` : ''
      }`;

      return (
        <Box onClick={handleLinkClick}>
          <Link to={newsUrl} variant="simple" stroke={false}>
            {content}
          </Link>
        </Box>
      );

    case 'community':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_manage':
      if (!payload?.community_id) return content;

      return (
        <Box onClick={handleLinkClick}>
          <Link
            to={`/${modules.community.basename}/${payload.community_id}/${payload.slug}/manage`}
            variant="simple"
            stroke={false}
          >
            {content}
          </Link>
        </Box>
      );

    case 'community_post':
    case 'community_post_comment':
      if (!payload?.community_id) return content;

      const postCommentUrl = [
        `/${modules.community.basename}`,
        `/${payload.community_id}`,
        '/post',
        `/${payload.post_id}`,
        payload.comment_id ? `#mention-${payload.comment_id}` : '',
      ].join('');

      return (
        <Box onClick={handleLinkClick}>
          <Link to={postCommentUrl} variant="simple" stroke={false}>
            {content}
          </Link>
        </Box>
      );
  }

  return content;
};
